import { For, Show } from "solid-js";
import { Icon } from "../../components/icon/Icon.tsx";
import TemplateSelector from "../../components/selectors/TemplateSelector.tsx";
import GenericFailureModal from "../GenericFailureModal.tsx";
import ApiService from "../../services/ApiService.ts";
import { useModalManager } from "../../components/modal/ModalManager.tsx";
import { useNavigate } from "@solidjs/router";
import { useCustomerState } from "../../state/customer.tsx";
import type { LanguageCode } from "../../state/i18n.tsx";
import { useTranslation } from "../../state/i18n.tsx";
import EmptyState from "../../components/emptyState/EmptyState.tsx";
import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "../../services/PostHogService.ts";
import type { TemplateFilter } from "./templateUtil.ts";
import { getAvailableTemplates } from "./templateUtil.ts";
import type FolderId from "../../state/FolderId.ts";
import { isEmpty, isNil } from "lodash-es";
import type { Folder } from "../../types/Folder.ts";
import type { Template } from "../../types/Template.ts";

type TemplatesProps = {
  query?: string;
  activeFilter: TemplateFilter;
  language: LanguageCode;
  onSelectSuccess?: () => void;
} & ({ onboarding: true } | { onboarding: false; folderId: FolderId });

const Templates = (props: TemplatesProps) => {
  const { t } = useTranslation();
  const createFormMutation = ApiService.createForm();
  const templates = ApiService.getTemplates(() => props.language);
  const createFolderMutation = ApiService.createFolder();
  const { open } = useModalManager();
  const navigate = useNavigate();
  const customer = useCustomerState();

  const createFolder = (onSuccess: (folder: Folder) => void) =>
    createFolderMutation.mutateAsync(
      { name: t(($) => $.ONBOARDING_PAGE_TEMPLATES_FIRST_FOLDER), icon: "folder" },
      {
        onSuccess,
        onError: (error) => {
          open({
            keepParent: true,
            content: () => (
              <GenericFailureModal
                title={t(($) => $.ONBOARDING_PAGE_CREATE_FOLDER_FAILURE_MODAL_TITLE)}
                error={error}
              />
            ),
          });
        },
      },
    );

  const createEmptyForm = (folderId: FolderId) =>
    createFormMutation.mutate(
      {
        createForm: {
          folderId,
          icon: "document",
          name: props.onboarding
            ? t(($) => $.ONBOARDING_PAGE_TEMPLATES_FIRST_FORM)
            : t(($) => $.FORM_CREATE_FORM_DEFAULT_NAME),
        },
        t,
      },
      {
        onSuccess: async (form) => {
          if (!isNil(props.onSelectSuccess)) {
            props.onSelectSuccess();
          }
          posthog.capture(POSTHOG_EVENTS.FORM_CREATED, {
            customerId: customer.customerId,
            folderId,
          });
          const formVersionId = await ApiService.resolveFormDraftId(form.id);
          navigate(`/customers/${customer.customerId}/forms/${form.id}/formVersions/${formVersionId}`);
        },
        onError: (error) => {
          open({
            keepParent: true,
            content: () => <GenericFailureModal title={t(($) => $.CREATE_FORM_FAILURE_MODAL_TITLE)} error={error} />,
          });
        },
      },
    );

  const createFormFromTemplate = (folderId: FolderId, template: Template) =>
    createFormMutation.mutate(
      {
        createForm: {
          templateId: template.id,
          publishedFormVersion: template.publishedVersion?.formVersionId,
          description: template.meta.description,
          tags: template.meta.tags,
          folderId,
          icon: template.meta.icon,
          name: template.meta.name,
        },
        t,
      },
      {
        onSuccess: async (form) => {
          if (!isNil(props.onSelectSuccess)) {
            props.onSelectSuccess();
          }
          posthog.capture(POSTHOG_EVENTS.FORM_CREATED, {
            customerId: customer.customerId,
            folderId,
            templateId: template.id,
            name: template.meta.name,
            tags: template.meta.tags,
          });
          const formVersionId = await ApiService.resolveFormDraftId(form.id);
          navigate(`/customers/${customer.customerId}/forms/${form.id}/formVersions/${formVersionId}`);
        },
        onError: (error) => {
          open({
            keepParent: true,
            content: () => <GenericFailureModal title={t(($) => $.CREATE_FORM_FAILURE_MODAL_TITLE)} error={error} />,
          });
        },
      },
    );

  return (
    /* All items in the scrollable region are focusable, no need to make the scrollable container focusable */
    <div class="grid grid-cols-3 gap-4" tabIndex={-1}>
      <Show
        when={!templates.isLoading && !createFormMutation.isPending}
        fallback={
          <div class="relative col-span-full flex h-14 w-full items-center justify-center px-1 py-4">
            <Icon name="spinner" size="xl" type="solid" class="animate-spin icon-secondary" />
            <h3 class="sr-only">{t(($) => $.ONBOARDING_PAGE_TEMPLATES_LOADING_ARIA_TEXT)}</h3>
          </div>
        }
      >
        <Show when={isNil(props.query) || isEmpty(props.query)}>
          <TemplateSelector
            title={t(($) => $.TEMPLATES_SCRATCH_CREATE_TITLE)}
            description={t(($) => $.TEMPLATES_SCRATCH_CREATE_DESCRIPTION)}
            onClick={async () => {
              if (props.onboarding) {
                createFolder((folder) => createEmptyForm(folder.id));
              } else {
                createEmptyForm(props.folderId);
              }
            }}
          />
        </Show>

        <For
          each={getAvailableTemplates(templates.data, props.activeFilter, props.query)}
          fallback={
            <Show when={props.query}>
              <EmptyState
                title={t(($) => $.TEMPLATES_SEARCH_NOT_FOUND_TITLE)}
                description={t(($) => $.TEMPLATES_SEARCH_NOT_FOUND_DESCRIPTION)}
                class="col-span-full"
              />
            </Show>
          }
        >
          {(template) => (
            <TemplateSelector
              tags={template.meta.tags}
              title={template.meta.name}
              description={template.meta.description}
              onClick={() => {
                if (props.onboarding) {
                  createFolder((folder) => createFormFromTemplate(folder.id, template));
                } else {
                  createFormFromTemplate(props.folderId, template);
                }
              }}
            />
          )}
        </For>
      </Show>
    </div>
  );
};

export default Templates;
